/**
 * Simple Split Test Infrastructure
 * 
 * Features:
 * - Unified system for all split tests
 * - Persistent user assignment using userDataPersistence
 * - Simple variant assignment (control/variant)
 * - Automatic event decoration
 * - Easy test management
 */

import { getUserData, storeUserData } from './storage/userDataService';

// Split test configuration
const SPLIT_TESTS = {
  default_quantity: {
    name: 'Book Page Default Quantity',
    variants: ['control', 'variant'],
    weight: 0.5, // 50% of users see variant (1 ticket)
  },
};

// User assignment storage key within userData
const SPLIT_TEST_KEY = 'splitTestAssignments';

/**
 * Get or create user's split test assignments
 */
function getUserAssignments() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const userData = getUserData();
    return userData?.[SPLIT_TEST_KEY] || {};
  } catch (error) {
    console.warn('Failed to load split test assignments:', error);
    return {};
  }
}

/**
 * Save user's split test assignments
 */
async function saveUserAssignments(assignments) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    await storeUserData({
      [SPLIT_TEST_KEY]: assignments,
    });
  } catch (error) {
    console.warn('Failed to save split test assignments:', error);
  }
}

/**
 * Generate a simple hash for consistent user assignment
 */
function hashUserId(userId) {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Import device fingerprinting from dedicated service
import { generateDeviceFingerprint } from './deviceFingerprinting';

/**
 * Get user ID for consistent assignment
 * Uses device fingerprint as primary identifier for privacy and consistency
 */
function getUserId() {
  // Always use device fingerprint for split test assignment
  // This ensures privacy, consistency, and works for all users
  return generateDeviceFingerprint();
}

/**
 * Get variant for a specific test
 */
function getVariant(testName) {
  const test = SPLIT_TESTS[testName];
  if (!test) {
    console.warn(`Unknown split test: ${testName}`);
    return 'control';
  }

  const assignments = getUserAssignments();
  
  // Return existing assignment if available
  if (assignments[testName]) {
    return assignments[testName];
  }

  // Generate new assignment
  const userId = getUserId();
  const hash = hashUserId(userId);
  const random = (hash % 100) / 100; // 0-1 based on user hash
  
  const variant = random < test.weight ? 'variant' : 'control';
  
  // Save assignment (fire and forget)
  assignments[testName] = variant;
  saveUserAssignments(assignments).catch(error => {
    console.warn('Failed to save split test assignment:', error);
  });
  
  return variant;
}

/**
 * Check if user is in variant for a test
 */
function isVariant(testName) {
  return getVariant(testName) === 'variant';
}

/**
 * Check if user is in control for a test
 */
function isControl(testName) {
  return getVariant(testName) === 'control';
}

/**
 * Decorate event data with split test metadata
 */
function decorateEventData(eventData = {}, testName) {
  const variant = getVariant(testName);
  return {
    ...eventData,
    split_test: testName,
    split_variant: variant,
  };
}

/**
 * Get all active split tests for debugging
 */
function getActiveTests() {
  const assignments = getUserAssignments();
  return Object.keys(SPLIT_TESTS).map(testName => ({
    test: testName,
    name: SPLIT_TESTS[testName].name,
    variant: assignments[testName] || 'unassigned',
    weight: SPLIT_TESTS[testName].weight,
  }));
}

/**
 * Reset all split test assignments (for testing)
 */
async function resetAssignments() {
  if (typeof window !== 'undefined') {
    try {
      await storeUserData({
        [SPLIT_TEST_KEY]: {},
      });
    } catch (error) {
      console.warn('Failed to reset split test assignments:', error);
    }
  }
}

export {
  getVariant,
  isVariant,
  isControl,
  decorateEventData,
  getActiveTests,
  resetAssignments,
  SPLIT_TESTS,
}; 